import {
  attachUserId,
  resetOptimizely,
  createUserProfileService,
  hasBeenActivated,
  createLogger,
  getVariation,
  isFeatureEnabled,
  getFeatureVariables,
} from '@updater-core/lib/optimizely';
import { track, ERROR } from '@updater-core/lib/tracker';
import isEqual from 'lodash/isEqual';
import { OptimizelyInteractor } from './interactor.d';

const { log } = createLogger('[SHELL]');

function setLocalOverridesFromUrl({ url: maybeUrl, createLocalGateway }) {
  try {
    const gateway = createLocalGateway();
    const url =
      Object.getPrototypeOf(maybeUrl)?.constructor === URL
        ? maybeUrl
        : new URL(maybeUrl);

    const experiment = url.searchParams.get('experiment');
    const variation = url.searchParams.get('variation');

    if (experiment && variation) {
      gateway.set(`upd_experiment.${experiment}`, variation);
    }

    const feature = url.searchParams.get('feature');
    const featureEnabled = url.searchParams.get('feature_enabled');

    const featureVariables = {};
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('feature_variable_')) {
        featureVariables[key.replace('feature_variable_', '')] = value;
      }
    });

    if (feature && featureEnabled) {
      gateway.set(
        `upd_feature.${feature}`,
        JSON.stringify({
          enabled: featureEnabled === 'true',
          ...(Object.keys(featureVariables).length > 0
            ? { variables: featureVariables }
            : {}),
        })
      );
    }

    if (url.searchParams.has('clear_experiments')) {
      gateway
        .keys()
        .filter(
          (key) =>
            key.startsWith('upd_experiment') || key.startsWith('upd_feature')
        )
        .forEach((key) => {
          gateway.remove(key);
        });
    }
  } catch (e) {
    log('Parsing URL params failed', e);
  }
}

export function buildOptimizely({
  interactors: { session },
  gateways: { createRemoteGateway, createLocalGateway },
  url,
}): OptimizelyInteractor {
  setLocalOverridesFromUrl({ url, createLocalGateway });

  let cachedHeaders = {};
  let initing = null;
  let userProfileService;
  let hasAttachedUserId;

  // eslint-disable-next-line no-underscore-dangle
  const _hasAttachedUserId = new Promise((resolve) => {
    hasAttachedUserId = resolve;
  });

  // eslint-disable-next-line @typescript-eslint/no-shadow
  async function init(session, state) {
    const uuid = session.selectors.uuid(state);
    const headers = session.selectors.requestHeaders(state);

    if (isEqual(headers, cachedHeaders)) return Promise.resolve();
    if (initing) return initing;

    cachedHeaders = headers;
    // eslint-disable-next-line no-async-promise-executor
    initing = new Promise<void>(async (resolve, reject) => {
      try {
        const gateway = createRemoteGateway(uuid, headers);
        userProfileService = await createUserProfileService(uuid, gateway);
        await resetOptimizely(userProfileService);
      } catch (e) {
        reject(e);
      }
      resolve();
    })
      .then(() => {
        log('DONE RESETTING');
        initing = null;
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Resetting optimizely failed', e);
        track({
          domain: 'mover',
          object: 'reset_optimizely',
          verb: ERROR,
          details: {
            error: e,
            message: e?.message,
            stack: e?.stack,
          },
        });
        initing = null;
      });
    return initing;
  }

  session.connect(async (state, done) => {
    const uuid = session.selectors.uuid(state);

    if (uuid) {
      done();
      if (createRemoteGateway) {
        await init(session, state);
      }
      attachUserId(uuid);
      hasAttachedUserId();
    }
  });

  return {
    actions: {
      init,
      hasBeenActivated: (...args) =>
        _hasAttachedUserId.then(() => hasBeenActivated(...args)),
      getVariation: (...args) =>
        _hasAttachedUserId.then(() => getVariation(...args)),
      isFeatureEnabled: (...args) =>
        _hasAttachedUserId.then(() => isFeatureEnabled(...args)),
      getFeatureVariables: (...args) =>
        _hasAttachedUserId.then(() => getFeatureVariables(...args)),
    },
    selectors: {
      ready: () => _hasAttachedUserId,
    },
  };
}
